import React, { useState } from 'react';
import { Box, chakra, HStack, Icon, Menu, Portal, Text } from '@chakra-ui/react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { LuCalendar, LuRotateCw, LuTimer } from 'react-icons/lu';
import { type DurationKey, type DurationOption } from '@/components/common/access-time-selector/types.ts';
import { TRANSITION_BORDER, TRANSITION_CHEVRON } from '@/components/ui/motion.ts';

interface DurationPillsProps {
	timeWindow: DurationKey;
	onSelect: (key: DurationKey) => void;
	options: DurationOption[];
	customLabel: string;
	headerLabel?: React.ReactNode;
}

const menuItemProps = (selected: boolean) => ({
	borderRadius: 0,
	px: 4,
	py: 3,
	textStyle: 'body.sm' as const,
	fontWeight: selected ? 600 : 400,
	bg: selected ? 'surface.subtle' : 'transparent',
	_hover: { bg: 'surface.subtle' },
});

const DurationPills: React.FC<DurationPillsProps> = ({ timeWindow, onSelect, options, customLabel, headerLabel }) => {
	const [showMoreDropdown, setShowMoreDropdown] = useState(false);

	const finiteOptions = options.filter((o) => o.value !== 'indefinite');
	const indefiniteOption = options.find((o) => o.value === 'indefinite');
	const customOption = { value: 'custom' as const, label: customLabel };

	const handleSelect = (value: DurationKey) => {
		onSelect(value);
		setShowMoreDropdown(false);
	};

	const getSelectedLabel = () => {
		const found = [...finiteOptions, ...(indefiniteOption ? [indefiniteOption] : []), customOption].find(
			(o) => o.value === timeWindow,
		);
		return found ? found.label : 'Select duration';
	};

	return (
		<HStack gap={3} align="center" justify="space-between" w="full">
			{headerLabel &&
				(typeof headerLabel === 'string' ? (
					<Text textStyle="label" color="fg.muted" flexShrink={0}>
						{headerLabel}
					</Text>
				) : (
					<Box flexShrink={0}>{headerLabel}</Box>
				))}

			<Box flexShrink={0} ml="auto">
				<Menu.Root
					open={showMoreDropdown}
					onOpenChange={({ open }) => setShowMoreDropdown(open)}
					// The access drawer body clips absolute children; portal and flip within the viewport.
					positioning={{
						placement: 'bottom-end',
						strategy: 'fixed',
						fitViewport: true,
						flip: true,
						gutter: 4,
					}}
				>
					<Menu.Trigger asChild>
						<chakra.button
							type="button"
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							gap={3}
							minH="42px"
							px={3}
							borderRadius="full"
							border="1px solid"
							borderColor="border.default"
							bg="surface.default"
							cursor="pointer"
							textStyle="body.sm"
							fontWeight={500}
							textAlign="left"
							whiteSpace="nowrap"
							transition={TRANSITION_BORDER}
							_hover={{ borderColor: 'border.strong' }}
							_focusVisible={{ outline: '2px solid', outlineColor: 'brand.700', outlineOffset: '2px' }}
						>
							{getSelectedLabel()}
							<Box
								as={FiChevronDown}
								boxSize="10px"
								transition={TRANSITION_CHEVRON}
								transform={showMoreDropdown ? 'rotate(180deg)' : 'rotate(0deg)'}
								flexShrink={0}
							/>
						</chakra.button>
					</Menu.Trigger>
					<Portal>
						<Menu.Positioner>
							<Menu.Content
								minW="220px"
								maxH="min(70svh, var(--available-height))"
								p={0}
								borderRadius="2xl"
								bg="surface.default"
							>
								<Box px={4} pt={4} pb={2}>
									<Text
										textStyle="micro"
										fontWeight="bold"
										color="fg.subtle"
										textTransform="uppercase"
										letterSpacing={0}
									>
										Expires after
									</Text>
								</Box>

								{finiteOptions.map((opt) => {
									const isSelected = timeWindow === opt.value;
									return (
										<Menu.Item
											key={opt.value}
											value={opt.value}
											onClick={() => handleSelect(opt.value)}
											{...menuItemProps(isSelected)}
										>
											<HStack gap={3} justify="space-between">
												<HStack gap={3}>
													<Icon as={LuTimer} boxSize="15px" color="fg.muted" />
													<Text as="span">{opt.label}</Text>
												</HStack>
												{isSelected && <Icon as={FiCheck} boxSize="15px" />}
											</HStack>
										</Menu.Item>
									);
								})}

								<Box borderTop="1px solid" borderColor="border.default" mx={3} my={1} />

								<Menu.Item
									value={customOption.value}
									onClick={() => handleSelect(customOption.value)}
									{...menuItemProps(timeWindow === customOption.value)}
								>
									<HStack gap={3} justify="space-between">
										<HStack gap={3}>
											<Icon as={LuCalendar} boxSize="15px" color="fg.muted" />
											<Text as="span">{customOption.label}</Text>
										</HStack>
										{timeWindow === customOption.value && <Icon as={FiCheck} boxSize="15px" />}
									</HStack>
								</Menu.Item>

								{indefiniteOption && (
									<>
										<Box borderTop="1px solid" borderColor="border.default" mx={3} my={1} />
										<Menu.Item
											value={indefiniteOption.value}
											onClick={() => handleSelect(indefiniteOption.value)}
											{...menuItemProps(timeWindow === indefiniteOption.value)}
										>
											<HStack gap={3} justify="space-between">
												<HStack gap={3}>
													<Icon as={LuRotateCw} boxSize="15px" color="fg.muted" />
													<Text as="span">{indefiniteOption.label}</Text>
												</HStack>
												{timeWindow === indefiniteOption.value && (
													<Icon as={FiCheck} boxSize="15px" />
												)}
											</HStack>
										</Menu.Item>
									</>
								)}
							</Menu.Content>
						</Menu.Positioner>
					</Portal>
				</Menu.Root>
			</Box>
		</HStack>
	);
};

export default DurationPills;
